import { useState, useCallback, useRef, useEffect } from 'react'
import './App.css'
import VideoUpload from './components/VideoUpload'
import VideoTrimmer from './components/VideoTrimmer'
import AnalysisResults from './components/AnalysisResults'
import FrameThumbnails from './components/FrameThumbnails'
import PromptPreview from './components/PromptPreview'
import { validateVideoFile, extractKeyframes, getVideoDuration } from './utils/videoUtils'
import { analyzeVideo, getContextPriming } from './utils/apiClient'
import { PoseExtractor } from './utils/poseExtractor'
import { downloadLLMAnalysis, createPoseSummaryForPrompt } from './utils/poseToLLMFormat'
import { buildPromptPreview } from './utils/promptBuilder'

function App() {
  const [videoFile, setVideoFile] = useState(null)
  const [videoUrl, setVideoUrl] = useState(null)
  const [videoDuration, setVideoDuration] = useState(0)
  const [trimRange, setTrimRange] = useState({ start: 0, end: 0 })
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [analysisResult, setAnalysisResult] = useState(null)
  const [extractedFrames, setExtractedFrames] = useState(null)
  const [error, setError] = useState(null)
  
  // Pose overlay state
  const [enablePoseOverlay, setEnablePoseOverlay] = useState(false)
  const [poseData, setPoseData] = useState(null)
  const [poseExtractorReady, setPoseExtractorReady] = useState(false)
  
  // Final prompt preview
  const [finalPrompt, setFinalPrompt] = useState(null)
  
  // Custom user prompt
  const [customPrompt, setCustomPrompt] = useState('')
  
  // Context priming state
  const [enableContextPriming, setEnableContextPriming] = useState(false)
  const [contextPrimingResult, setContextPrimingResult] = useState(null)
  const [isContextPriming, setIsContextPriming] = useState(false)
  
  const videoUploadRef = useRef(null)
  const poseExtractorRef = useRef(null)

  // Initialize pose extractor when overlay is enabled
  useEffect(() => {
    if (enablePoseOverlay && !poseExtractorRef.current) {
      const initPoseExtractor = async () => {
        try {
          console.log('Initializing MediaPipe PoseLandmarker...')
          const extractor = new PoseExtractor()
          await extractor.init()
          poseExtractorRef.current = extractor
          setPoseExtractorReady(true)
          console.log('✅ PoseExtractor ready')
        } catch (err) {
          console.error('Failed to initialize PoseExtractor:', err)
          setError('Failed to load pose detection model. Please try again.')
          setEnablePoseOverlay(false)
        }
      }
      initPoseExtractor()
    }

    // Cleanup on unmount
    return () => {
      if (poseExtractorRef.current) {
        poseExtractorRef.current.dispose()
        poseExtractorRef.current = null
      }
    }
  }, [enablePoseOverlay])

  // Handle pose overlay toggle
  const handlePoseOverlayToggle = () => {
    setEnablePoseOverlay(prev => !prev)
    if (enablePoseOverlay) {
      // Turning off - clear pose data
      setPoseData(null)
    }
  }

  // Download pose JSON
  const handleDownloadPoseJSON = () => {
    if (poseData && poseData.length > 0) {
      const filename = videoFile?.name?.replace(/\.[^/.]+$/, '') || 'video'
      downloadLLMAnalysis(poseData, filename)
    }
  }

  const handleVideoSelect = async (file) => {
    // Validate the video file
    const validation = validateVideoFile(file)
    if (!validation.isValid) {
      setError(validation.error)
      return
    }

    setVideoFile(file)
    setVideoUrl(URL.createObjectURL(file))
    setAnalysisResult(null)
    setExtractedFrames(null)
    setError(null)

    // Get video duration for trimmer
    try {
      const duration = await getVideoDuration(file)
      setVideoDuration(duration)
      setTrimRange({ start: 0, end: duration })
    } catch (err) {
      console.warn('Could not get video duration:', err)
      setVideoDuration(0)
    }
  }

  const handleTrimChange = useCallback((range) => {
    setTrimRange(range)
  }, [])

  const handleSeekVideo = useCallback((time) => {
    videoUploadRef.current?.seekTo(time)
  }, [])

  const handleStartAnalysis = async () => {
    if (!videoFile) {
      setError('Please upload a video first')
      return
    }

    setIsAnalyzing(true)
    setError(null)
    setExtractedFrames(null) // Clear previous frames
    setPoseData(null) // Clear previous pose data
    setFinalPrompt(null) // Clear previous prompt
    setContextPrimingResult(null) // Clear previous context

    try {
      // Extract keyframes from video within trimmed range
      console.log('Extracting keyframes from video...')
      console.log(`Trim range: ${trimRange.start.toFixed(2)}s - ${trimRange.end.toFixed(2)}s`)
      console.log(`Pose overlay enabled: ${enablePoseOverlay}`)
      console.log(`Context priming enabled: ${enableContextPriming}`)
      
      const extractionResult = await extractKeyframes(videoFile, {
        maxFrames: 10,  // Extract 10 frames
        quality: 0.8,   // 80% JPEG quality
        trimStart: trimRange.start,
        trimEnd: trimRange.end,
        enablePoseOverlay: enablePoseOverlay,
        poseExtractor: enablePoseOverlay && poseExtractorReady ? poseExtractorRef.current : null
      })
      
      // extractKeyframes now returns { frames, poseFrames } when pose overlay is enabled
      const frames = extractionResult.frames || extractionResult
      const poseFrames = extractionResult.poseFrames || null
      
      console.log(`Extracted ${frames.length} frames`)
      if (poseFrames) {
        console.log(`Extracted ${poseFrames.length} pose frames`)
        setPoseData(poseFrames)
      }
      
      // Store extracted frames for display
      setExtractedFrames(frames)
      
      // Prepare pose analysis for API if enabled
      let poseAnalysis = null
      if (enablePoseOverlay && poseFrames && poseFrames.length > 0) {
        poseAnalysis = createPoseSummaryForPrompt(poseFrames, videoFile.name)
      }
      
      // Get context priming if enabled (uses first frame only)
      let contextInfo = null
      if (enableContextPriming && frames.length > 0) {
        setIsContextPriming(true)
        console.log('Running context priming on first frame...')
        try {
          contextInfo = await getContextPriming(frames[0])
          setContextPrimingResult(contextInfo)
          console.log('Context priming result:', contextInfo)
        } catch (contextErr) {
          console.error('Context priming failed:', contextErr)
          // Continue with analysis even if context priming fails
          setError(`Context priming failed: ${contextErr.message}. Continuing with main analysis...`)
        } finally {
          setIsContextPriming(false)
        }
      }
      
      // Build and store the final prompt for preview
      const promptPreview = buildPromptPreview(frames.length, poseAnalysis, customPrompt, contextInfo)
      setFinalPrompt(promptPreview)
      
      // Send frames to Cloudflare Worker via API client
      const result = await analyzeVideo(frames, videoFile.name, poseAnalysis, customPrompt, contextInfo)
      setAnalysisResult(result)
    } catch (err) {
      setError(err.message || 'Analysis failed. Please try again.')
      console.error('Analysis error:', err)
      setExtractedFrames(null) // Clear frames on error
    } finally {
      setIsAnalyzing(false)
      setIsContextPriming(false)
    }
  }

  const handleStop = () => {
    setIsAnalyzing(false)
  }

  const handleReset = () => {
    setVideoFile(null)
    setVideoUrl(null)
    setVideoDuration(0)
    setTrimRange({ start: 0, end: 0 })
    setAnalysisResult(null)
    setExtractedFrames(null)
    setError(null)
    setPoseData(null)
    setFinalPrompt(null)
    setCustomPrompt('')
    setContextPrimingResult(null)
  }

  return (
    <div className="app">
      <header className="app-header">
        <h1>🏀 Video Analysis AI</h1>
        <p>Upload your video for AI-powered analysis and insights</p>
      </header>

      <div className="app-content">
        <div className="video-section">
          <VideoUpload
            ref={videoUploadRef}
            videoUrl={videoUrl}
            onVideoSelect={handleVideoSelect}
            isAnalyzing={isAnalyzing}
            enablePoseOverlay={enablePoseOverlay}
            poseExtractor={poseExtractorReady ? poseExtractorRef.current : null}
          />

          {videoUrl && videoDuration > 0 && (
            <VideoTrimmer
              videoUrl={videoUrl}
              duration={videoDuration}
              onTrimChange={handleTrimChange}
              onSeek={handleSeekVideo}
            />
          )}
          
          {/* Pose Overlay Toggle */}
          <div className="pose-overlay-toggle">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={enablePoseOverlay}
                onChange={handlePoseOverlayToggle}
                disabled={isAnalyzing}
              />
              <span className="toggle-switch"></span>
              <span className="toggle-text">
                🦴 Pose Skeleton Overlay
                {enablePoseOverlay && !poseExtractorReady && ' (Loading...)'}
              </span>
            </label>
            <p className="toggle-description">
              When enabled, skeleton pose data is overlaid on video frames and sent to AI for enhanced analysis
            </p>
          </div>

          {/* Context Priming Toggle */}
          <div className="pose-overlay-toggle">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={enableContextPriming}
                onChange={() => setEnableContextPriming(prev => !prev)}
                disabled={isAnalyzing}
              />
              <span className="toggle-switch"></span>
              <span className="toggle-text">
                🔍 Context Priming
                {isContextPriming && ' (Analyzing...)'}
              </span>
            </label>
            <p className="toggle-description">
              Pre-analyze the first frame to understand player age, setting, and conditions for tailored coaching feedback
            </p>
          </div>

          {/* Context Priming Result Display */}
          {contextPrimingResult && (
            <div className="context-priming-result">
              <div className="context-priming-header">
                <span className="context-priming-icon">🔍</span>
                <span className="context-priming-title">Scene Context</span>
              </div>
              <p className="context-priming-text">{contextPrimingResult}</p>
            </div>
          )}

          <div className="controls">
            <button
              className="btn btn-primary"
              onClick={handleStartAnalysis}
              disabled={!videoFile || isAnalyzing || (enablePoseOverlay && !poseExtractorReady)}
            >
              {isContextPriming ? '🔍 PRIMING CONTEXT...' : isAnalyzing ? '🔄 ANALYZING...' : '▶️ START ANALYSIS'}
            </button>
            
            <button
              className="btn btn-stop"
              onClick={handleStop}
              disabled={!isAnalyzing}
            >
              ⏹️ STOP
            </button>
            
            <button
              className="btn btn-secondary"
              onClick={handleReset}
            >
              🔄 RESET
            </button>
          </div>

          {/* Custom Prompt Input */}
          <div className="custom-prompt-section">
            <label className="custom-prompt-label">
              <span className="custom-prompt-title">✏️ Custom Instructions (Optional)</span>
              <textarea
                className="custom-prompt-input"
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder="Add custom instructions for the AI analysis, e.g., &quot;Provide feedback in the style of a friendly youth coach, focusing on encouragement and simple tips for beginners.&quot;"
                disabled={isAnalyzing}
                rows={3}
              />
            </label>
            <p className="custom-prompt-description">
              Customize the tone, detail level, or focus of the AI feedback. Leave empty for default analysis.
            </p>
          </div>

          {/* Download Pose JSON Button */}
          {poseData && poseData.length > 0 && (
            <div className="pose-download">
              <button
                className="btn btn-download"
                onClick={handleDownloadPoseJSON}
              >
                📥 Download Pose JSON ({poseData.length} frames)
              </button>
            </div>
          )}

          {error && (
            <div className="error-message">
              ⚠️ {error}
            </div>
          )}

          <FrameThumbnails frames={extractedFrames} />
          
          <PromptPreview prompt={finalPrompt} frameCount={extractedFrames?.length || 0} />
        </div>

        <div className="results-section">
          {analysisResult ? (
            <AnalysisResults result={analysisResult} />
          ) : (
            <div className="placeholder">
              <div className="placeholder-content">
                <div className="placeholder-icon">📊</div>
                <h2>No Analysis Yet</h2>
                <p>Upload a video and click "Start Analysis" to get AI-powered insights</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default App

